<template>
  <div class="paper-page page-wrapper">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">摘要提交</h1>
        <p class="page-subtitle">Paper Submission</p>
      </div>
    </div>

    <div class="page-content">
      <div class="container">

        <!-- 投稿表单 -->
        <section class="paper-form-section">
          <h2 class="section-title">提交摘要</h2>
          
          <div v-if="!isLoggedIn" class="login-tip">
            <el-alert
              title="请先登录后再提交摘要"
              type="warning"
              :closable="false"
              show-icon
            >
              <template slot="title">
                <span>请先 <router-link to="/login">登录</router-link> 或 <router-link to="/register">注册</router-link> 后再提交摘要</span>
              </template>
            </el-alert>
          </div>

          <el-form 
            v-else
            ref="paperForm" 
            :model="paperForm" 
            :rules="paperRules" 
            label-width="100px"
            class="paper-form"
          >
            <el-form-item label="摘要标题" prop="title">
              <el-input 
                v-model="paperForm.title" 
                placeholder="请输入摘要标题"
                maxlength="200"
                show-word-limit
              ></el-input>
            </el-form-item>

            <el-form-item label="作者信息" prop="authors">
              <el-input 
                v-model="paperForm.authors" 
                placeholder="请输入作者姓名，多个作者用逗号分隔"
              ></el-input>
            </el-form-item>

            <el-form-item label="摘要内容" prop="abstract">
              <el-input 
                type="textarea" 
                v-model="paperForm.abstract" 
                placeholder="请输入摘要内容"
                :rows="6"
                maxlength="1000"
                show-word-limit
              ></el-input>
            </el-form-item>

            <el-form-item>
              <el-button type="primary" :loading="submitting" @click="submitPaper">
                提交摘要
              </el-button>
              <el-button @click="resetForm">重置</el-button>
            </el-form-item>
          </el-form>
        </section>

        <!-- 公开摘要列表 -->
        <section class="paper-list-section">
          <h2 class="section-title">已录用摘要</h2>
          <!-- <p class="section-desc">以下摘要已通过审核，可供下载阅读</p> -->

          <div v-if="loading" class="loading-state">
            <el-skeleton :rows="4" animated />
          </div>
          
          <div v-else-if="publicPapers.length === 0" class="empty-state">
            <i class="el-icon-document-delete"></i>
            <p>暂无公开摘要</p>
          </div>

          <div v-else>
            <div class="paper-list">
              <div v-for="paper in publicPapers" :key="paper.id" class="paper-item">
                <div class="paper-info" @click="showPaperDetail(paper)">
                  <h4 class="paper-title">{{ paper.title }}</h4>
                  <div class="paper-meta">
                    <span class="paper-authors"><i class="el-icon-user"></i> {{ paper.authors }}</span>
                    <span class="paper-keywords"><i class="el-icon-price-tag"></i> {{ paper.keywords }}</span>
                  </div>
                  <p class="paper-abstract">{{ paper.abstract }}</p>
                </div>
                <div class="paper-action">
                  <el-button type="primary" size="small" icon="el-icon-view" @click="showPaperDetail(paper)">
                    查看详情
                  </el-button>
                </div>
              </div>
            </div>
            
            <!-- 分页 -->
            <div class="pagination-wrapper">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="currentPage"
                :page-sizes="[5, 10, 20, 50]"
                :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="total"
                background
              >
              </el-pagination>
            </div>
          </div>
        </section>
        
        <!-- 摘要详情弹窗 -->
        <el-dialog
          title="摘要详情"
          :visible.sync="detailDialogVisible"
          width="70%"
          :before-close="closeDetailDialog"
          class="paper-detail-dialog"
        >
          <div v-if="selectedPaper" class="paper-detail-content">
            <h3 class="detail-title">{{ selectedPaper.title }}</h3>
            <div class="detail-meta">
              <p><strong>作者：</strong>{{ selectedPaper.authors }}</p>
              <!-- <p><strong>关键词：</strong>{{ selectedPaper.keywords }}</p> -->
              <p><strong>提交时间：</strong>{{ formatDateTime(selectedPaper.createtime) }}</p>
            </div>
            <div class="detail-abstract">
              <h4>摘要内容：</h4>
              <p>{{ selectedPaper.abstract }}</p>
            </div>
            <!-- <div class="detail-file" v-if="selectedPaper.file">
              <h4>摘要文件：</h4>
              <el-button type="primary" size="small" @click="downloadPaper(selectedPaper)">
                下载文件
              </el-button>
            </div> -->
          </div>
        </el-dialog>
      </div>
    </div>
  </div>
</template>

<script>
import { paperApi } from '@/api'

export default {
  name: 'Paper',
  data() {
    return {
      submitting: false,
      loading: false,
      currentPage: 1,
      pageSize: 5,
      total: 0,
      publicPapers: [],
      detailDialogVisible: false,
      selectedPaper: null,
      paperForm: {
        title: '',
        authors: '',
        abstract: ''
      },
      paperRules: {
        title: [
          { required: true, message: '请输入摘要标题', trigger: 'blur' }
        ],
        authors: [
          { required: true, message: '请输入作者信息', trigger: 'blur' }
        ],
        abstract: [
          { required: true, message: '请输入摘要摘要', trigger: 'blur' }
        ]
      },
    }
  },
  
  // 在组件挂载后加载公开摘要列表
  mounted() {
    this.loadPublicPapers();
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('token')
    }
  },
  methods: {

    // 加载公开摘要列表
    async loadPublicPapers() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.pageSize
        };
        const response = await paperApi.getPublicPapers(params);
        
        if (response && response.data && response.data.list) {
          this.publicPapers = response.data.list;
          this.total = response.data.total || 0;
        } else {
          this.publicPapers = [];
          this.total = 0;
        }
      } catch (error) {
        console.error('获取公开摘要列表失败:', error);
        this.$message.error('获取摘要列表失败');
        this.publicPapers = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    
    async submitPaper() {
      this.$refs.paperForm.validate(async (valid) => {
        if (!valid) return

        this.submitting = true

        try {
          // 提交摘要信息
          const submitData = {
            ...this.paperForm,
            conference_id: 1
          }

          await paperApi.submit(submitData)
          
          this.$message.success('摘要提交成功，请等待审核')
          this.resetForm()
          
          // 重新加载公开摘要列表
          this.loadPublicPapers();
        } catch (error) {
          console.error('提交失败:', error)
          this.$message.error(error.message || '提交失败')
        } finally {
          this.submitting = false
        }
      })
    },
    resetForm() {
      this.$refs.paperForm.resetFields()
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1; // 重置到第一页
      this.loadPublicPapers();
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadPublicPapers();
    },
    showPaperDetail(paper) {
      this.selectedPaper = paper;
      this.detailDialogVisible = true;
    },
    closeDetailDialog() {
      this.detailDialogVisible = false;
      this.selectedPaper = null;
    },
    async downloadPaper(paper) {
      try {
        const res = await paperApi.download(paper.id)
        // 处理下载
        if (res && res.data && res.data.url) {
          window.open(res.data.url, '_blank')
        } else {
          this.$message.error('文件下载链接无效')
        }
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败')
      }
    },
    // 格式化时间戳为年月日时分秒
    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      
      // 如果是秒级时间戳（10位），转换为毫秒级
      let date = new Date(timestamp)
      if (timestamp.toString().length === 10) {
        date = new Date(timestamp * 1000)
      }
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style lang="scss" scoped>
.paper-page {
  background: #f8f9fc;
}

.pagination-wrapper {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

.paper-item {
  cursor: pointer;
  
  &:hover {
    .paper-info {
      background: #f0f2f8;
    }
  }
}

.paper-detail-content {
  .detail-title {
    font-size: 20px;
    font-weight: bold;
    color: #1a237e;
    margin-bottom: 20px;
  }
  
  .detail-meta {
    margin-bottom: 20px;
    
    p {
      margin: 8px 0;
      
      strong {
        color: #333;
      }
    }
  }
  
  .detail-abstract {
    margin-bottom: 20px;
    
    h4 {
      color: #1a237e;
      margin-bottom: 10px;
    }
    
    p {
      line-height: 1.8;
      color: #666;
    }
  }
  
  .detail-file {
    h4 {
      color: #1a237e;
      margin-bottom: 10px;
    }
  }
}

.loading-state {
  padding: 30px 0;
}

.empty-state {
  text-align: center;
  padding: 60px 0;
  color: #999;
  
  i {
    font-size: 64px;
    margin-bottom: 16px;
  }
  
  p {
    font-size: 16px;
  }
}

.paper-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.paper-item {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 24px;
  background: #f8f9fc;
  border-radius: 12px;
  transition: all 0.3s;

  &:hover {
    background: #f0f2f8;
  }

  .paper-info {
    flex: 1;
  }

  .paper-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a237e;
    margin-bottom: 12px;
  }

  .paper-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 12px;
    font-size: 14px;
    color: #666;

    i {
      margin-right: 6px;
      color: #7c4dff;
    }
  }

  .paper-abstract {
    font-size: 14px;
    color: #888;
    line-height: 1.8;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .paper-action {
    flex-shrink: 0;
  }
}

.paper-form {
  max-width: 700px;
}

.paper-upload {
  width: 100%;

  ::v-deep .el-upload {
    width: 100%;
  }

  ::v-deep .el-upload-dragger {
    width: 100%;
    height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a237e;
  margin-bottom: 40px;
}

.section-desc {
  color: #666;
  margin-bottom: 30px;
}

.paper-form-section {
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 50px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.login-tip {
  margin-bottom: 30px;

  a {
    color: #1a237e;
    font-weight: 500;
  }
}

.paper-list-section {
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

@media screen and (max-width: 992px) {
  .paper-intro {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 576px) {
  .paper-form-section,
  .paper-list-section {
    padding: 24px;
  }

  .paper-item {
    flex-direction: column;
    gap: 16px;
  }
}
</style>
